import * as THREE from 'three'
import {
  getGSplatBatchInstanceCount,
  setBatchedSplatQuadGeometry,
} from '@/plugins/gsplat/core/gsplatBatching'
import { getLoggerManager } from '@/shared/logging'
import type { SplatData } from './SplatData'

const log = getLoggerManager().getLogger('gsplat:core')

/**
 * 将 float32 转换为 half float (16位)
 * 参考：https://stackoverflow.com/questions/32633585/how-do-you-convert-a-float-to-a-half-precision-float
 */
function float2Half(value: number): number {
  const floatView = new Float32Array(1)
  const int32View = new Int32Array(floatView.buffer)

  floatView[0] = value
  const x = int32View[0]

  let bits = (x >> 16) & 0x8000 // 符号位
  let m = (x >> 12) & 0x07ff // 尾数
  const e = (x >> 23) & 0xff // 指数

  if (e < 103) {
    // 太小，变成 0
    return bits
  }

  if (e > 142) {
    // 太大或 NaN/Inf
    bits |= 0x7c00
    bits |= (e === 255 ? 0 : 1) && x & 0x007fffff
    return bits
  }

  if (e < 113) {
    // 次正规数
    m |= 0x0800
    bits |= (m >> (114 - e)) + ((m >> (113 - e)) & 1)
    return bits
  }

  bits |= ((e - 112) << 10) | (m >> 1)
  bits += m & 1
  return bits
}

/**
 * 高斯点云几何体
 *
 * 核心职责：
 * 1. 创建用于实例化渲染的四边形几何体
 * 2. 将 SplatData 打包成 GPU 纹理格式（半精度优化）
 * 3. 管理排序索引纹理
 *
 * 内存布局（参考 PlayCanvas 优化方案）：
 * - transformA (RGBA32U): xyz 位置 (float32) + rotation.xy 打包 (2 x half)
 * - transformB (RGBA16F): scale.xyz (half) + rotation.z (half)
 * - 总计: 24 bytes/splat（相比原来的 48 bytes 节省 50%）
 */
export class GaussianSplatGeometry extends THREE.InstancedBufferGeometry {
  /** 位置(xyz) + rotation.xy 打包纹理 */
  dataTextureA!: THREE.DataTexture
  /** 缩放(xyz) + rotation.z 纹理 */
  dataTextureB!: THREE.DataTexture
  /** 颜色(RGB) + 不透明度(A) 纹理 */
  colorTexture!: THREE.DataTexture
  /** 排序索引纹理（uint32） */
  orderTexture!: THREE.DataTexture

  /** SH scale + c0-c2 纹理，RGBA32UI，band ≥ 1 时生成 */
  sh1to3Texture?: THREE.DataTexture
  /** SH c3-c6 纹理，RGBA32UI，band ≥ 2 时生成 */
  sh4to7Texture?: THREE.DataTexture
  /** SH c7-c10 纹理，RGBA32UI 或 R32UI，band ≥ 2 时生成 */
  sh8to11Texture?: THREE.DataTexture
  /** SH c11-c14 纹理，RGBA32UI，仅 band 3 */
  sh12to15Texture?: THREE.DataTexture
  /** 兼容旧接口的 SH 纹理数组 */
  shTextures: THREE.DataTexture[] = []
  /** SH 阶数 0~3 */
  shBands: number = 0

  /** 数据纹理宽度（像素） */
  dataWidth: number = 0
  /** 数据纹理高度（像素） */
  dataHeight: number = 0
  /** 排序纹理宽度（像素） */
  orderWidth: number = 0
  /** 排序纹理高度（像素） */
  orderHeight: number = 0
  /** 当前可见 splat 数量，区别于 batched draw instance 数量 */
  visibleSplatCount: number = 0

  /**
   * 构造函数
   * @param splatData 原始 Splat 数据
   */
  constructor(splatData: SplatData) {
    super()

    // 1. 创建基础四边形几何体
    this.createQuadGeometry()

    // 2. 设置可见 splat 数和 batched draw instance 数
    this.setVisibleSplatCount(splatData.numSplats)

    // 3. 检测 SH 阶数
    this.shBands = splatData.shBands

    // 4. 创建数据纹理
    this.createDataTextures(splatData)

    // 5. 创建 SH 纹理（如果有）
    if (this.shBands > 0) {
      this.createSHTextures(splatData)
    }

    // 6. 创建排序纹理（初始为顺序索引）
    this.createOrderTexture(splatData.numSplats)
  }

  /**
   * 创建四边形几何体
   * Quad 顶点覆盖 [-1, 1] 范围，在 Vertex Shader 中会被缩放到椭圆大小
   */
  private createQuadGeometry(): void {
    setBatchedSplatQuadGeometry(this)
  }

  /**
   * 设置当前可见 splat 数，并同步 batched draw instance 数。
   *
   * @param count - 当前可见 splat 数量
   */
  setVisibleSplatCount(count: number): void {
    this.visibleSplatCount = Math.max(0, Math.floor(count))
    this.instanceCount = getGSplatBatchInstanceCount(this.visibleSplatCount)
  }

  /**
   * 计算纹理尺寸
   * @param count 数据数量
   * @returns [width, height]
   */
  private evalTextureSize(count: number): [number, number] {
    const width = Math.ceil(Math.sqrt(count))
    const height = Math.ceil(count / width)
    return [width, height]
  }

  /**
   * 创建数据纹理（半精度优化版）
   *
   * 内存布局（参考 PlayCanvas）：
   * - transformA (RGBA32U): xyz 位置 (float32 as uint32) + rotation.xy 打包 (2 x half)
   * - transformB (RGBA16F): scale.xyz (half) + rotation.z (half)
   *
   * @param splatData 原始数据
   */
  private createDataTextures(splatData: SplatData): void {
    const numSplats = splatData.numSplats
    const [width, height] = this.evalTextureSize(numSplats)
    this.dataWidth = width
    this.dataHeight = height

    // 获取原始属性数组
    const x = splatData.getProp('x') as Float32Array
    const y = splatData.getProp('y') as Float32Array
    const z = splatData.getProp('z') as Float32Array

    const rot_0 = splatData.getProp('rot_0') as Float32Array
    const rot_1 = splatData.getProp('rot_1') as Float32Array
    const rot_2 = splatData.getProp('rot_2') as Float32Array
    const rot_3 = splatData.getProp('rot_3') as Float32Array

    const scale_0 = splatData.getProp('scale_0') as Float32Array
    const scale_1 = splatData.getProp('scale_1') as Float32Array
    const scale_2 = splatData.getProp('scale_2') as Float32Array

    const f_dc_0 = splatData.getProp('f_dc_0') as Float32Array
    const f_dc_1 = splatData.getProp('f_dc_1') as Float32Array
    const f_dc_2 = splatData.getProp('f_dc_2') as Float32Array
    const opacity = splatData.getProp('opacity') as Float32Array

    // 验证必需属性
    if (!x || !y || !z || !rot_0 || !rot_1 || !rot_2 || !rot_3) {
      throw new Error('Missing required properties: position or rotation')
    }
    if (!scale_0 || !scale_1 || !scale_2) {
      throw new Error('Missing required properties: scale')
    }
    if (!f_dc_0 || !f_dc_1 || !f_dc_2 || !opacity) {
      throw new Error('Missing required properties: color or opacity')
    }

    // ========================================================
    // Texture A: 位置 + 旋转前半部分 (RGBA32U)
    // 内存布局：
    // - R: position.x (float32 as uint32)
    // - G: position.y (float32 as uint32)
    // - B: position.z (float32 as uint32)
    // - A: rotation.x (half) | rotation.y (half) << 16
    //
    // 每个 splat 使用 16 bytes（4 x uint32）
    // ========================================================
    const dataA = new Uint32Array(width * height * 4)
    const dataAFloat = new Float32Array(dataA.buffer)

    for (let i = 0; i < numSplats; i++) {
      const offset = i * 4

      // 位置直接存储（float32 的位模式与 uint32 共享内存）
      dataAFloat[offset + 0] = x[i]
      dataAFloat[offset + 1] = y[i]
      dataAFloat[offset + 2] = z[i]

      // 归一化四元数
      // 注意：PLY 存储格式是 rot_0=w, rot_1=x, rot_2=y, rot_3=z (标量在前)
      let qw = rot_0[i]
      const qx = rot_1[i]
      const qy = rot_2[i]
      const qz = rot_3[i]
      const qLen = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw)
      let rx = qx / qLen
      let ry = qy / qLen
      let rz = qz / qLen
      qw = qw / qLen

      // 确保 w >= 0（四元数双覆盖，选择 w >= 0 的那个）
      if (qw < 0) {
        rx = -rx
        ry = -ry
        rz = -rz
        // qw = -qw; // 不需要存储 w
      }

      // 打包 rotation.x 和 rotation.y 到一个 uint32
      // 低 16 位: rx (half), 高 16 位: ry (half)
      dataA[offset + 3] = float2Half(rx) | (float2Half(ry) << 16)
    }

    this.dataTextureA = new THREE.DataTexture(
      dataA,
      width,
      height,
      THREE.RGBAIntegerFormat,
      THREE.UnsignedIntType
    )
    this.dataTextureA.internalFormat = 'RGBA32UI'
    this.dataTextureA.minFilter = THREE.NearestFilter
    this.dataTextureA.magFilter = THREE.NearestFilter
    this.dataTextureA.wrapS = THREE.ClampToEdgeWrapping
    this.dataTextureA.wrapT = THREE.ClampToEdgeWrapping
    this.dataTextureA.needsUpdate = true

    // ========================================================
    // Texture B: 缩放 + 旋转后半部分 (RGBA16F)
    // 内存布局：
    // - R: scale.x (half float)
    // - G: scale.y (half float)
    // - B: scale.z (half float)
    // - A: rotation.z (half float)
    //
    // 每个 splat 使用 8 bytes（4 x half）
    // ========================================================
    const dataB = new Uint16Array(width * height * 4)

    for (let i = 0; i < numSplats; i++) {
      const offset = i * 4

      // 归一化四元数（与上面相同）
      // 注意：PLY 存储格式是 rot_0=w, rot_1=x, rot_2=y, rot_3=z (标量在前)
      let qw = rot_0[i]
      const qx = rot_1[i]
      const qy = rot_2[i]
      const qz = rot_3[i]
      const qLen = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw)
      let rz = qz / qLen
      qw = qw / qLen

      // 确保 w >= 0
      if (qw < 0) {
        rz = -rz
      }

      // 缩放使用 exp 激活（从 log space 转换）
      dataB[offset + 0] = float2Half(Math.exp(scale_0[i]))
      dataB[offset + 1] = float2Half(Math.exp(scale_1[i]))
      dataB[offset + 2] = float2Half(Math.exp(scale_2[i]))
      dataB[offset + 3] = float2Half(rz)
    }

    this.dataTextureB = new THREE.DataTexture(
      dataB,
      width,
      height,
      THREE.RGBAFormat,
      THREE.HalfFloatType
    )
    this.dataTextureB.internalFormat = 'RGBA16F'
    this.dataTextureB.minFilter = THREE.NearestFilter
    this.dataTextureB.magFilter = THREE.NearestFilter
    this.dataTextureB.wrapS = THREE.ClampToEdgeWrapping
    this.dataTextureB.wrapT = THREE.ClampToEdgeWrapping
    this.dataTextureB.needsUpdate = true

    // ========================================================
    // Color Texture: 颜色 + 不透明度 (RGBA16F)
    // SH DC 系数转 RGB: rgb = f_dc * SH_C0 + 0.5
    // SH_C0 = 0.28209479177387814
    //
    // 重要：与 PlayCanvas gsplat-resource.js 保持一致，使用 RGBA16F
    // 半精度浮点可保留更多颜色精度，避免 RGBA8 量化导致的渲染模糊
    // ========================================================
    const SH_C0 = 0.28209479177387814
    const colorData = new Uint16Array(width * height * 4)

    for (let i = 0; i < numSplats; i++) {
      const offset = i * 4

      // SH DC 转 RGB（与 PlayCanvas gsplat-resource.js:149-152 一致）
      const r = f_dc_0[i] * SH_C0 + 0.5
      const g = f_dc_1[i] * SH_C0 + 0.5
      const b = f_dc_2[i] * SH_C0 + 0.5

      // Opacity: sigmoid 激活 (1 / (1 + exp(-x)))
      const alpha = 1.0 / (1.0 + Math.exp(-opacity[i]))

      // 转换为半精度浮点（与 PlayCanvas 一致）
      colorData[offset + 0] = float2Half(r)
      colorData[offset + 1] = float2Half(g)
      colorData[offset + 2] = float2Half(b)
      colorData[offset + 3] = float2Half(alpha)
    }

    this.colorTexture = new THREE.DataTexture(
      colorData,
      width,
      height,
      THREE.RGBAFormat,
      THREE.HalfFloatType
    )
    this.colorTexture.internalFormat = 'RGBA16F'
    this.colorTexture.colorSpace = THREE.LinearSRGBColorSpace // 明确标记为线性色彩空间
    this.colorTexture.minFilter = THREE.NearestFilter
    this.colorTexture.magFilter = THREE.NearestFilter
    this.colorTexture.wrapS = THREE.ClampToEdgeWrapping
    this.colorTexture.wrapT = THREE.ClampToEdgeWrapping
    this.colorTexture.needsUpdate = true
  }

  /**
   * 创建排序索引纹理
   * @param count Splat 数量
   */
  private createOrderTexture(count: number): void {
    const [width, height] = this.evalTextureSize(count)
    this.orderWidth = width
    this.orderHeight = height

    // 初始化为顺序索引: [0, 1, 2, 3, ...]
    const orderData = new Uint32Array(width * height)
    for (let i = 0; i < count; i++) {
      orderData[i] = i
    }

    this.orderTexture = new THREE.DataTexture(
      orderData,
      width,
      height,
      THREE.RedIntegerFormat,
      THREE.UnsignedIntType
    )
    this.orderTexture.internalFormat = 'R32UI' // 明确指定内部格式
    this.orderTexture.minFilter = THREE.NearestFilter
    this.orderTexture.magFilter = THREE.NearestFilter
    this.orderTexture.wrapS = THREE.ClampToEdgeWrapping
    this.orderTexture.wrapT = THREE.ClampToEdgeWrapping
    this.orderTexture.needsUpdate = true
  }

  /**
   * 创建球谐（SH）纹理 - 完全对标 PlayCanvas
   *
   * 使用 11-10-11 位打包方案压缩 SH 系数：
   * - 每个 splat 计算所有 SH 系数的最大绝对值（scale）
   * - 将系数归一化到 [-1, 1] 范围
   * - 量化为 11-10-11 位（R:11, G:10, B:11）打包到 uint32
   *
   * 纹理布局（对标 PlayCanvas gsplat-resource.js）：
   * - sh1to3Texture: [scale(float32), c0(11-10-11), c1(11-10-11), c2(11-10-11)]
   * - sh4to7Texture: [c3, c4, c5, c6]
   * - sh8to11Texture: [c7, c8, c9, c10] (band 3) 或 [c7] (band 2, R32UI)
   * - sh12to15Texture: [c11, c12, c13, c14] (band 3)
   *
   * @param splatData 原始数据
   */
  private createSHTextures(splatData: SplatData): void {
    const numSplats = splatData.numSplats
    const [width, height] = this.evalTextureSize(numSplats)

    // 根据 bands 确定每个通道的系数数量
    // 1 band = 3 coeffs/channel, 2 bands = 8 coeffs/channel, 3 bands = 15 coeffs/channel
    const numCoeffsMap: Record<number, number> = { 1: 3, 2: 8, 3: 15 }
    const numCoeffs = numCoeffsMap[this.shBands]

    // 预加载所有 f_rest_ 属性
    const src: (Float32Array | null)[] = []
    for (let i = 0; i < numCoeffs * 3; i++) {
      src.push(splatData.getProp(`f_rest_${i}`) as Float32Array | null)
    }

    // 位量化常量
    const t11 = (1 << 11) - 1 // 2047
    const t10 = (1 << 10) - 1 // 1023

    // 用于 float32 <-> uint32 转换
    const float32 = new Float32Array(1)
    const uint32 = new Uint32Array(float32.buffer)

    // 系数缓存
    const c = new Array(numCoeffs * 3).fill(0)

    // 创建纹理数据
    const sh1to3Data = new Uint32Array(width * height * 4)
    const sh4to7Data = this.shBands > 1 ? new Uint32Array(width * height * 4) : null
    const sh8to11Data =
      this.shBands > 2
        ? new Uint32Array(width * height * 4)
        : this.shBands > 1
          ? new Uint32Array(width * height) // R32UI for band 2
          : null
    const sh12to15Data = this.shBands > 2 ? new Uint32Array(width * height * 4) : null

    for (let i = 0; i < numSplats; i++) {
      // 提取系数（PlayCanvas 布局: [r0,r1,r2,..., g0,g1,g2,..., b0,b1,b2,...]）
      for (let j = 0; j < numCoeffs; j++) {
        c[j * 3 + 0] = src[j]?.[i] ?? 0 // R
        c[j * 3 + 1] = src[j + numCoeffs]?.[i] ?? 0 // G
        c[j * 3 + 2] = src[j + numCoeffs * 2]?.[i] ?? 0 // B
      }

      // 计算最大绝对值
      let max = Math.abs(c[0])
      for (let j = 1; j < numCoeffs * 3; j++) {
        max = Math.max(max, Math.abs(c[j]))
      }

      if (max === 0) {
        // 跳过全零系数
        continue
      }

      // 归一化并量化为 11-10-11 bit
      for (let j = 0; j < numCoeffs; j++) {
        c[j * 3 + 0] = Math.max(
          0,
          Math.min(t11, Math.floor(((c[j * 3 + 0] / max) * 0.5 + 0.5) * t11 + 0.5))
        )
        c[j * 3 + 1] = Math.max(
          0,
          Math.min(t10, Math.floor(((c[j * 3 + 1] / max) * 0.5 + 0.5) * t10 + 0.5))
        )
        c[j * 3 + 2] = Math.max(
          0,
          Math.min(t11, Math.floor(((c[j * 3 + 2] / max) * 0.5 + 0.5) * t11 + 0.5))
        )
      }

      // 打包
      float32[0] = max

      // sh1to3: [max, c0, c1, c2]
      sh1to3Data[i * 4 + 0] = uint32[0]
      sh1to3Data[i * 4 + 1] = (c[0] << 21) | (c[1] << 11) | c[2]
      sh1to3Data[i * 4 + 2] = (c[3] << 21) | (c[4] << 11) | c[5]
      sh1to3Data[i * 4 + 3] = (c[6] << 21) | (c[7] << 11) | c[8]

      if (this.shBands > 1 && sh4to7Data) {
        // sh4to7: [c3, c4, c5, c6]
        sh4to7Data[i * 4 + 0] = (c[9] << 21) | (c[10] << 11) | c[11]
        sh4to7Data[i * 4 + 1] = (c[12] << 21) | (c[13] << 11) | c[14]
        sh4to7Data[i * 4 + 2] = (c[15] << 21) | (c[16] << 11) | c[17]
        sh4to7Data[i * 4 + 3] = (c[18] << 21) | (c[19] << 11) | c[20]

        if (this.shBands > 2 && sh8to11Data && sh12to15Data) {
          // sh8to11: [c7, c8, c9, c10]
          sh8to11Data[i * 4 + 0] = (c[21] << 21) | (c[22] << 11) | c[23]
          sh8to11Data[i * 4 + 1] = (c[24] << 21) | (c[25] << 11) | c[26]
          sh8to11Data[i * 4 + 2] = (c[27] << 21) | (c[28] << 11) | c[29]
          sh8to11Data[i * 4 + 3] = (c[30] << 21) | (c[31] << 11) | c[32]

          // sh12to15: [c11, c12, c13, c14]
          sh12to15Data[i * 4 + 0] = (c[33] << 21) | (c[34] << 11) | c[35]
          sh12to15Data[i * 4 + 1] = (c[36] << 21) | (c[37] << 11) | c[38]
          sh12to15Data[i * 4 + 2] = (c[39] << 21) | (c[40] << 11) | c[41]
          sh12to15Data[i * 4 + 3] = (c[42] << 21) | (c[43] << 11) | c[44]
        } else if (sh8to11Data) {
          // band 2: 只需要 1 个通道
          sh8to11Data[i] = (c[21] << 21) | (c[22] << 11) | c[23]
        }
      }
    }

    // 创建纹理
    this.sh1to3Texture = new THREE.DataTexture(
      sh1to3Data,
      width,
      height,
      THREE.RGBAIntegerFormat,
      THREE.UnsignedIntType
    )
    this.sh1to3Texture.internalFormat = 'RGBA32UI'
    this.sh1to3Texture.minFilter = THREE.NearestFilter
    this.sh1to3Texture.magFilter = THREE.NearestFilter
    this.sh1to3Texture.wrapS = THREE.ClampToEdgeWrapping
    this.sh1to3Texture.wrapT = THREE.ClampToEdgeWrapping
    this.sh1to3Texture.needsUpdate = true
    this.shTextures.push(this.sh1to3Texture)

    if (this.shBands > 1 && sh4to7Data) {
      this.sh4to7Texture = new THREE.DataTexture(
        sh4to7Data,
        width,
        height,
        THREE.RGBAIntegerFormat,
        THREE.UnsignedIntType
      )
      this.sh4to7Texture.internalFormat = 'RGBA32UI'
      this.sh4to7Texture.minFilter = THREE.NearestFilter
      this.sh4to7Texture.magFilter = THREE.NearestFilter
      this.sh4to7Texture.wrapS = THREE.ClampToEdgeWrapping
      this.sh4to7Texture.wrapT = THREE.ClampToEdgeWrapping
      this.sh4to7Texture.needsUpdate = true
      this.shTextures.push(this.sh4to7Texture)

      if (this.shBands > 2 && sh8to11Data && sh12to15Data) {
        // band 3: RGBA32UI
        this.sh8to11Texture = new THREE.DataTexture(
          sh8to11Data,
          width,
          height,
          THREE.RGBAIntegerFormat,
          THREE.UnsignedIntType
        )
        this.sh8to11Texture.internalFormat = 'RGBA32UI'
        this.sh8to11Texture.minFilter = THREE.NearestFilter
        this.sh8to11Texture.magFilter = THREE.NearestFilter
        this.sh8to11Texture.wrapS = THREE.ClampToEdgeWrapping
        this.sh8to11Texture.wrapT = THREE.ClampToEdgeWrapping
        this.sh8to11Texture.needsUpdate = true
        this.shTextures.push(this.sh8to11Texture)

        this.sh12to15Texture = new THREE.DataTexture(
          sh12to15Data,
          width,
          height,
          THREE.RGBAIntegerFormat,
          THREE.UnsignedIntType
        )
        this.sh12to15Texture.internalFormat = 'RGBA32UI'
        this.sh12to15Texture.minFilter = THREE.NearestFilter
        this.sh12to15Texture.magFilter = THREE.NearestFilter
        this.sh12to15Texture.wrapS = THREE.ClampToEdgeWrapping
        this.sh12to15Texture.wrapT = THREE.ClampToEdgeWrapping
        this.sh12to15Texture.needsUpdate = true
        this.shTextures.push(this.sh12to15Texture)
      } else if (sh8to11Data) {
        // band 2: R32UI
        this.sh8to11Texture = new THREE.DataTexture(
          sh8to11Data,
          width,
          height,
          THREE.RedIntegerFormat,
          THREE.UnsignedIntType
        )
        this.sh8to11Texture.internalFormat = 'R32UI'
        this.sh8to11Texture.minFilter = THREE.NearestFilter
        this.sh8to11Texture.magFilter = THREE.NearestFilter
        this.sh8to11Texture.wrapS = THREE.ClampToEdgeWrapping
        this.sh8to11Texture.wrapT = THREE.ClampToEdgeWrapping
        this.sh8to11Texture.needsUpdate = true
        this.shTextures.push(this.sh8to11Texture)
      }
    }
  }

  /**
   * 更新排序纹理
   * @param sortedIndices 排序后的索引数组
   */
  updateOrder(sortedIndices: Uint32Array): void {
    if (sortedIndices.length !== this.visibleSplatCount) {
      log.error('Sorted indices length mismatch')
      return
    }

    // 更新纹理数据
    const data = this.orderTexture.image.data as Uint32Array
    data.set(sortedIndices)
    this.orderTexture.minFilter = THREE.NearestFilter
    this.orderTexture.magFilter = THREE.NearestFilter
    this.orderTexture.wrapS = THREE.ClampToEdgeWrapping
    this.orderTexture.wrapT = THREE.ClampToEdgeWrapping
    this.orderTexture.needsUpdate = true
  }

  /**
   * 释放所有资源
   */
  dispose(): void {
    this.dataTextureA?.dispose()
    this.dataTextureB?.dispose()
    this.colorTexture?.dispose()
    this.orderTexture?.dispose()

    // 释放 SH 纹理
    this.sh1to3Texture?.dispose()
    this.sh4to7Texture?.dispose()
    this.sh8to11Texture?.dispose()
    this.sh12to15Texture?.dispose()
    this.shTextures = []

    super.dispose()
  }
}
